// Salary cap draft utilities

export const REFERENCE_SALARY_CAP = 200;
const REFERENCE_MAX_PLAYER_SALARY = 50;

// Generate projected auction salary scaled to the league's salary cap budget
export function generatePlayerSalary(player, salaryCap = REFERENCE_SALARY_CAP) {
  const cap = Math.max(1, Number(salaryCap) || REFERENCE_SALARY_CAP);
  const scale = cap / REFERENCE_SALARY_CAP;
  const avgPoints = player.avg_fantasy_points || 0;
  const position = player.position;

  const positionMultipliers = {
    QB: 1.2,
    RB: 1.1,
    WR: 1.0,
    TE: 0.9,
    K: 0.5,
  };

  const baseSalary = Math.max(1, Math.floor(avgPoints * 2 * scale));
  let salary = Math.floor(baseSalary * (positionMultipliers[position] || 1.0));
  const maxSingle = Math.max(1, Math.floor(REFERENCE_MAX_PLAYER_SALARY * scale));
  salary = Math.max(1, Math.min(salary, maxSingle));

  return salary;
}

// Calculate total salary for a roster
export function calculateRosterSalary(roster) {
  return roster.reduce((total, player) => {
    // Use salaryCapPrice if available (actual amount paid), otherwise use salary (estimated)
    return total + (player.salaryCapPrice || player.salary || 0);
  }, 0);
}

// Check if a team can afford a player
export function canAffordPlayer(team, player, salaryCap) {
  const currentSalary = calculateRosterSalary(team.roster);
  const playerSalary = player.salary || generatePlayerSalary(player, salaryCap);
  
  // Calculate remaining roster spots (assuming 12 total roster spots)
  const remainingSpots = 12 - team.roster.length;
  
  // Always reserve $1 per remaining spot to ensure roster completion
  const reservedBudget = remainingSpots;
  
  return (currentSalary + playerSalary) <= (salaryCap - reservedBudget);
}

// Get remaining budget for a team
export function getRemainingBudget(team, salaryCap) {
  // If team has a budget property (from manual bidding), use that
  if (team.budget !== undefined) {
    return Math.max(0, team.budget);
  }
  
  // Otherwise calculate from roster salary cap prices
  const currentSalary = calculateRosterSalary(team.roster);
  
  // Calculate remaining roster spots (assuming 12 total roster spots)
  const remainingSpots = 12 - team.roster.length;
  
  // Always reserve $1 per remaining spot to ensure roster completion
  const reservedBudget = remainingSpots;
  
  return Math.max(0, salaryCap - currentSalary - reservedBudget);
}

// Format salary for display
export function formatSalary(salary) {
  return `$${salary}`;
}

// Get salary cap status for a team
export function getSalaryCapStatus(team, salaryCap) {
  const currentSalary = calculateRosterSalary(team.roster);
  
  // Calculate remaining roster spots (assuming 12 total roster spots)
  const remainingSpots = 12 - team.roster.length;
  const reservedBudget = remainingSpots;
  
  const availableBudget = salaryCap - reservedBudget;
  const remaining = Math.max(0, availableBudget - currentSalary);
  const percentage = (currentSalary / availableBudget) * 100;
  
  return {
    current: currentSalary,
    remaining: remaining,
    percentage: percentage,
    isOver: currentSalary > availableBudget,
    isNearLimit: percentage >= 90,
    reservedBudget: reservedBudget,
    availableBudget: availableBudget
  };
} 